import {
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges,
  ViewEncapsulation,
} from "@angular/core";
import { CommonModule } from "@angular/common";
import { FormsModule } from "@angular/forms";

import {
  DropdownModule,
  DropdownMenuData,
  DropdownMenuItem,
} from "@vg-constellation/angular-17/dropdown";
import { InputComponent } from "@vg-constellation/angular-17/input";
import { LabelComponent } from "@vg-constellation/angular-17/label";
import { ButtonComponent } from "@vg-constellation/angular-17/button";
import { LinkComponent, LinkModule } from "@vg-constellation/angular-17/link";
import { HintErrorComponent } from "@vg-constellation/angular-17/hint-error";
import { IconModule } from "@vg-constellation/angular-17/icon";

import { BeneficiaryType } from "../../../types/BeneficiaryType";
import {
  Beneficiary,
  BeneficiarySectionType,
  DropdownStateChangeEvent,
  EditTrustBeneficiaryPayload,
  getTrustLabel,
} from "@models/beneficiary.model";
import { Trust } from "@models/estateProfile.model";
import { WebLogger } from "@weps/shared-http";
import * as content from "@services/content/guided-flow/account-details-content";
import {
  filterSettlorsForTrustType,
  resolveSettlorOnTypeChange,
} from "@services/utils/trust-settlor.utils";

type TrustFormState = "trust-dropdown" | "trust-inline-form";

@Component({
  selector: "app-trust-input-form",
  standalone: true,
  encapsulation: ViewEncapsulation.None,
  imports: [
    CommonModule,
    FormsModule,
    DropdownModule,
    InputComponent,
    LabelComponent,
    ButtonComponent,
    LinkComponent,
    LinkModule,
    HintErrorComponent,
    IconModule,
  ],
  templateUrl: "./trust-input-form.component.html",
  styleUrls: ["./trust-input-form.component.scss"],
})
export class TrustInputFormComponent implements OnInit, OnChanges {
  private logEvent(
    eventType: string,
    payload: Record<string, unknown> = {},
    level: "info" | "warn" | "error" = "info",
  ): void {
    WebLogger.log({
      message: `TrustInputForm: ${eventType}`,
      level,
      payload: { event_type: eventType, ...payload },
    });
  }

  @Input() mode: "add" | "edit" = "add";
  @Input() existingData: Beneficiary | null = null;
  @Input() trusts: Trust[] = [];
  @Input() settlors: DropdownMenuItem[] = [];
  @Input() section: BeneficiarySectionType = "PRIMARY";
  @Input() isSaving = false;
  @Input() showChangeType = true;

  @Output() save = new EventEmitter<Beneficiary>();
  @Output() saveEditTrust = new EventEmitter<EditTrustBeneficiaryPayload>();
  @Output() changeType = new EventEmitter<void>();
  @Output() cancel = new EventEmitter<void>();

  protected readonly content = content;

  // Internal state machine
  currentState: TrustFormState = "trust-dropdown";

  // Trust inline form fields
  trustDisplayName = "";
  trustLegalName = "";
  trustType = "";
  trustSettlor = "";

  // Validation flags
  validTrustLegalName = true;
  validTrustType = true;
  validTrustSettlor = true;

  // Trust dropdown data
  trustDropdownData: DropdownMenuData = { groups: [] };

  // Settlor dropdown data
  settlorOptions: DropdownMenuData = { groups: [] };

  // Trust type static options
  trustTypeOptions: DropdownMenuData = {
    groups: [
      {
        items: [
          { labelText: "Revocable", id: "REVOCABLE" },
          { labelText: "Irrevocable", id: "IRREVOCABLE" },
        ],
      },
    ],
  };

  // Track if trust select error should be shown
  showTrustSelectError = false;

  ngOnInit(): void {
    this.buildTrustDropdown();
    this.buildSettlorOptions();

    if (this.mode === "edit" && this.existingData?.householdMemberId) {
      this.populateFromExistingTrust();
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes["trusts"] && !changes["trusts"].firstChange) {
      this.buildTrustDropdown();
      // Re-populate if trusts arrived after initial edit attempt
      if (
        this.mode === "edit" &&
        this.existingData?.householdMemberId &&
        this.currentState !== "trust-inline-form"
      ) {
        this.populateFromExistingTrust();
      }
    }
    if (changes["settlors"] && !changes["settlors"].firstChange) {
      this.buildSettlorOptions();
    }
  }

  private buildTrustDropdown(): void {
    const items: DropdownMenuItem[] = [
      { labelText: content.BENEFICIARY_ADD_NEW_TRUST, id: "ADD_NEW_TRUST" },
    ];
    for (const trust of this.trusts) {
      if (trust.householdMemberId) {
        items.push({
          labelText: getTrustLabel(trust),
          id: trust.householdMemberId,
        });
      }
    }
    this.trustDropdownData = { groups: [{ items }] };
  }

  private buildSettlorOptions(): void {
    const filtered = filterSettlorsForTrustType(this.settlors, this.trustType);
    this.settlorOptions = { groups: [{ items: filtered }] };
  }

  private populateFromExistingTrust(): void {
    const trust = this.trusts.find(
      (t) => t.householdMemberId === this.existingData!.householdMemberId,
    );
    if (!trust) {
      this.logEvent(
        "trust_form_edit_trust_not_found",
        { member_id: this.existingData!.householdMemberId },
        "warn",
      );
      return;
    }

    this.trustLegalName = trust.legalName || "";
    this.trustDisplayName = trust.displayName || "";
    this.trustType =
      trust.trustType === "Revocable"
        ? "REVOCABLE"
        : trust.trustType === "Irrevocable"
          ? "IRREVOCABLE"
          : trust.trustType || "";
    this.trustSettlor = this.deriveSettlorId(trust.settlors);

    this.validTrustLegalName = true;
    this.validTrustType = true;
    this.validTrustSettlor = true;

    this.buildSettlorOptions();
    this.currentState = "trust-inline-form";

    this.logEvent("trust_form_edit_populated", {
      trust_id: this.existingData!.householdMemberId,
      trust_type: this.trustType,
    });
  }

  private deriveSettlorId(settlors: { memberType: string; householdMemberId: string }[]): string {
    if (!settlors || settlors.length === 0) return "";
    if (settlors.length > 1) return "JOINT";

    const settlorId = settlors[0].householdMemberId;
    const matchingOption = this.settlors.find((s) => s.id === settlorId);
    if (matchingOption) return settlorId;

    return "PRIMARY";
  }

  handleTrustDropdownChange(event: DropdownStateChangeEvent): void {
    const selectedId = event?.activeItems?.[0];
    if (!selectedId) return;
    this.showTrustSelectError = false;
    if (selectedId === "ADD_NEW_TRUST") {
      this.logEvent("trust_form_add_new_trust", { section: this.section });
      this.currentState = "trust-inline-form";
      return;
    }
    this.logEvent("trust_form_existing_trust_selected", {
      trust_id: selectedId,
      section: this.section,
    });
    const bene: Beneficiary = {
      kind: "existing",
      householdMemberId: selectedId,
      type: this.section,
      distributionPercentage: 0,
      beneficiaryType: BeneficiaryType.TRUST,
    };
    this.save.emit(bene);
  }

  handleTrustSave(): void {
    if (!this.validateTrustForm()) {
      this.logEvent(
        "trust_form_save_validation_failed",
        {
          valid_legal_name: this.validTrustLegalName,
          valid_trust_type: this.validTrustType,
          valid_settlor: this.validTrustSettlor,
        },
        "warn",
      );
      return;
    }

    const trustData = {
      legalName: this.trustLegalName.trim(),
      displayName: this.trustDisplayName.trim(),
      trustType: this.trustType,
      settlor: this.trustSettlor,
    };

    // Edit mode: emit trust edit payload
    if (this.mode === "edit" && this.existingData?.householdMemberId) {
      const payload: EditTrustBeneficiaryPayload = {
        beneficiary: {
          kind: "existing",
          householdMemberId: this.existingData.householdMemberId,
          type: this.section,
          distributionPercentage: this.existingData.distributionPercentage,
          beneficiaryType: BeneficiaryType.TRUST,
        },
        trustData,
      };
      this.logEvent("trust_form_save_edit_emitted", {
        member_id: payload.beneficiary.householdMemberId,
        section: payload.beneficiary.type,
        trust_type: this.trustType,
      });
      this.saveEditTrust.emit(payload);
      return;
    }

    // Add mode: create new trust beneficiary
    const bene: Beneficiary = {
      kind: "new",
      householdMemberId: null,
      trustData,
      type: this.section,
      distributionPercentage: 0,
      beneficiaryType: BeneficiaryType.TRUST,
    };
    this.logEvent("trust_form_save_emitted", {
      kind: bene.kind,
      section: bene.type,
      trust_type: this.trustType,
    });
    this.save.emit(bene);
  }

  validateTrustForm(): boolean {
    this.validTrustLegalName = !!this.trustLegalName.trim();
    this.validTrustType = !!this.trustType;
    this.validTrustSettlor = !!this.trustSettlor;
    return this.validTrustLegalName && this.validTrustType && this.validTrustSettlor;
  }

  validateTrustLegalName(): void {
    this.validTrustLegalName = !!this.trustLegalName.trim();
  }

  handleTrustTypeChange(event: DropdownStateChangeEvent): void {
    this.trustType = event?.activeItems?.[0] || "";
    this.validTrustType = !!this.trustType;
    this.trustSettlor = resolveSettlorOnTypeChange(this.trustType, this.trustSettlor);
    this.buildSettlorOptions();
  }

  handleTrustSettlorChange(event: DropdownStateChangeEvent): void {
    this.trustSettlor = event?.activeItems?.[0] || "";
    this.validTrustSettlor = !!this.trustSettlor;
  }

  resetTrustForm(): void {
    this.trustDisplayName = "";
    this.trustLegalName = "";
    this.trustType = "";
    this.trustSettlor = "";
    this.validTrustLegalName = true;
    this.validTrustType = true;
    this.validTrustSettlor = true;
  }
}
